// ============================================================
// ObjectSection - what an Object body is beside its Wounds and Armour (2026-10-26, board 7).
//
// Whether it COUNTS AS TERRAIN - and then the box it fills (metres) and what it is made of - and what it leaves when
// destroyed. A box is never judged against a map here: a sheet travels between scenes, so the map that draws it is
// the one that refuses a box too large for it ("Too large for this map").
// ============================================================
import type { Character } from '../../engine/character/types';
import { DEFAULT_MATERIALS } from '../../engine/battlefield/voxels';
import { readObjectSpec, type ObjectSpec } from '../../engine/objects/objectSpec';
import clsx from '../../utils/clsx';
import ReadoutNumber from './ReadoutNumber';
import SheetSwitch from './SheetSwitch';
import styles from './Sheet.module.css';

interface Props {
  character: Character;
  onChange: (partial: Partial<Character>) => void;
  disabled: boolean;
}

const TERRAIN_TIP = 'Its body is a box on the map: it blocks sight, gives cover and stops movement';
/** A box for an object first made terrain: a metre cube, the GM's to reshape. */
const FIRST_BOX = { w: 1, d: 1, h: 1 };
const SIDES = [
  ['w', 'Width'],
  ['d', 'Depth'],
  ['h', 'Height'],
] as const;
/** Solid stuff only: a box of smoke or water is not a thing to destroy. */
const MATERIALS = Object.values(DEFAULT_MATERIALS).filter((m) => !m.permeability);
const REMAINS: { value: ObjectSpec['remains']; label: string }[] = [
  { value: 'rubble', label: 'Rubble' },
  { value: 'gone', label: 'Nothing' },
];

interface BoxProps {
  spec: ObjectSpec;
  set: (patch: Partial<ObjectSpec>) => void;
  disabled: boolean;
}

function BoxRow({ spec, set, disabled }: BoxProps) {
  const box = spec.box ?? FIRST_BOX;
  return (
    <div className={styles.factRow}>
      {SIDES.map(([side, name]) => (
        <ReadoutNumber
          key={side}
          label={`${name} (m)`}
          ariaLabel={`Object ${name.toLowerCase()} in metres`}
          testId={`object-box-${side}`}
          min={0.1}
          step={0.5}
          float
          value={box[side]}
          disabled={disabled}
          onChange={(value) => value > 0 && set({ box: { ...box, [side]: value } })}
        />
      ))}
      <label className={styles.readout}>
        <span className={styles.readoutLabel}>Material</span>
        <select
          className={styles.select}
          data-testid="object-material"
          value={spec.material}
          disabled={disabled}
          onChange={(e) => set({ material: e.target.value })}
        >
          {MATERIALS.map((m) => (
            <option key={m.key} value={m.key}>
              {m.name}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

export default function ObjectSection({ character, onChange, disabled }: Props) {
  const spec = readObjectSpec(character.objectSpec);
  const set = (patch: Partial<ObjectSpec>) => onChange({ objectSpec: { ...spec, ...patch } });
  return (
    <div data-testid="object-section">
      <SheetSwitch
        label="Counts as terrain"
        title={TERRAIN_TIP}
        checked={spec.terrain}
        disabled={disabled}
        onChange={(terrain) => set(terrain ? { terrain, box: spec.box ?? FIRST_BOX } : { terrain })}
      />
      {spec.terrain && <BoxRow spec={spec} set={set} disabled={disabled} />}
      <div className={styles.choiceRow} role="group" aria-label="When destroyed">
        <span className={styles.readoutLabel}>Leaves</span>
        {REMAINS.map(({ value, label }) => (
          <label key={value} className={clsx(styles.choice, spec.remains === value && styles.choiceOn)}>
            <input
              type="radio"
              name="objectRemains"
              data-testid={`object-remains-${value}`}
              checked={spec.remains === value}
              disabled={disabled}
              onChange={() => set({ remains: value })}
            />
            {label}
          </label>
        ))}
      </div>
    </div>
  );
}
